import React, { useState, useEffect } from 'react';
import { Cookie, X, Settings as SettingsIcon, Shield } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const CookieConsent: React.FC = () => {
    const { lang } = useLanguage();
    const [isVisible, setIsVisible] = useState(false);
    const [showSettings, setShowSettings] = useState(false);

    // Localized translations
    const t = {
        en: {
            title: "We value your privacy",
            desc1: "We use cookies to ensure the secure and proper functioning of the Lashing Calculator platform and, with your consent, to analyse usage and improve performance.",
            desc2: "You can accept all cookies, reject non-essential cookies, or manage your preferences at any time. For more information, please see our ",
            cookiePolicy: "Cookie Policy",
            privacyPolicy: "Privacy Policy",
            acceptAll: "Accept all cookies",
            rejectAll: "Reject non-essential",
            managePref: "Manage preferences",
            prefTitle: "Cookie Preferences",
            prefDesc: "Cookies help us operate the Platform securely and understand how it is used. You can control which optional cookies you allow below.",
            alwaysActive: "Always Active",
            necessaryTitle: "Strictly Necessary Cookies",
            necessaryDesc: "These cookies are essential for authentication, security, session management, and core platform functionality. They cannot be disabled.",
            necessaryAlert: "Strictly necessary cookies are always enabled as they are required for the Platform to function.",
            analyticsTitle: "Analytics Cookies",
            analyticsDesc: "These cookies help us understand how users interact with the Platform so we can improve performance and usability.",
            analyticsAlert: "Analytics cookies are used only with your explicit consent.",
            savePref: "Save preferences",
            withdrawAlert: "You can change or withdraw your cookie consent at any time through this panel. For more details, please read our "
        },
        tr: {
            title: "Gizliliğinize değer veriyoruz",
            desc1: "Lashing Calculator platformunun güvenli ve düzgün çalışmasını sağlamak ve izninizle kullanım analizleri yaparak performansı artırmak için çerezleri kullanıyoruz.",
            desc2: "Tüm çerezleri kabul edebilir, zorunlu olmayan çerezleri reddedebilir veya tercihlerinizi dilediğiniz an yönetebilirsiniz. Detaylı bilgi için lütfen ",
            cookiePolicy: "Çerez Politikası",
            privacyPolicy: "Gizlilik Politikası",
            acceptAll: "Tümünü kabul et",
            rejectAll: "Zorunlu olmayanları reddet",
            managePref: "Tercihleri yönet",
            prefTitle: "Çerez Tercihleri",
            prefDesc: "Çerezler, Platformu güvenli bir şekilde işletmemize ve nasıl kullanıldığını anlamamıza yardımcı olur. İzin verdiğiniz isteğe bağlı çerezleri aşağıdan kontrol edebilirsiniz.",
            alwaysActive: "Her Zaman Aktif",
            necessaryTitle: "Zorunlu Teknik Çerezler",
            necessaryDesc: "Bu çerezler kimlik doğrulama, güvenlik, oturum yönetimi ve platformun temel işlevleri için gereklidir. Devre dışı bırakılamazlar.",
            necessaryAlert: "Zorunlu çerezler, Platformun çalışması için gerekli olduğundan her zaman etkindir.",
            analyticsTitle: "Analiz Çerezleri",
            analyticsDesc: "Bu çerezler, kullanıcıların Platform ile nasıl etkileşime girdiğini anlamamıza yardımcı olarak performansı ve kullanılabilirliği geliştirmemizi sağlar.",
            analyticsAlert: "Analiz çerezleri yalnızca sizin açık rızanızla kullanılır.",
            savePref: "Tercihleri kaydet",
            withdrawAlert: "Çerez izninizi bu panel üzerinden dilediğiniz zaman değiştirebilir veya geri çekebilirsiniz. Detaylar için lütfen "
        }
    };

    const currentTexts = t[lang as 'en' | 'tr'] || t.en;

    // Consent state
    const [preferences, setPreferences] = useState({
        essential: true, // Always true and disabled
        analytics: false,
        marketing: false
    });

    useEffect(() => {
        // Check if user has already consented
        const consent = localStorage.getItem('cookieConsent');
        if (!consent) {
            // Small delay for better UX
            const timer = setTimeout(() => setIsVisible(true), 1000);
            return () => clearTimeout(timer);
        }
    }, []);

    // Listen for custom event to open settings
    useEffect(() => {
        const handleOpenSettings = () => {
            setIsVisible(true);
            setShowSettings(true);
        };
        window.addEventListener('openCookieSettings', handleOpenSettings);
        return () => window.removeEventListener('openCookieSettings', handleOpenSettings);
    }, []);

    const handleAcceptAll = () => {
        const allConsented = { essential: true, analytics: true, marketing: true };
        saveConsent(allConsented);
    };

    const handleRejectAll = () => {
        const rejected = { essential: true, analytics: false, marketing: false };
        saveConsent(rejected);
    };

    const handleSavePreferences = () => {
        saveConsent(preferences);
    };

    const saveConsent = (settings: typeof preferences) => {
        localStorage.setItem('cookieConsent', JSON.stringify({
            ...settings,
            timestamp: new Date().toISOString()
        }));
        setIsVisible(false);
        // Small delay to ensure state update before closing logic if needed
        setTimeout(() => setShowSettings(false), 300);
    };

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    initial={{ y: 20, opacity: 0, scale: 0.95 }}
                    animate={{ y: 0, opacity: 1, scale: 1 }}
                    exit={{ y: 20, opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className="fixed bottom-6 right-6 z-[100] w-full max-w-[480px] pointer-events-none"
                >
                    <div className="bg-slate-950/90 backdrop-blur-2xl border border-white/10 rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] pointer-events-auto overflow-hidden ring-1 ring-white/5">
                        {!showSettings ? (
                            // Main Banner View
                            <div className="p-7">
                                <div className="flex items-start gap-5 mb-6">
                                    <div className="flex-shrink-0 relative">
                                        <div className="absolute inset-0 bg-orange-500/20 blur-xl rounded-full animate-pulse"></div>
                                        <div className="relative bg-gradient-to-br from-slate-800 to-slate-900 p-3.5 rounded-2xl border border-white/10">
                                            <Cookie className="w-6 h-6 text-orange-500" />
                                        </div>
                                    </div>

                                    <div className="flex-grow space-y-2">
                                        <h3 className="text-xl font-bold text-white tracking-tight">{currentTexts.title}</h3>
                                        <p className="text-slate-300 text-sm leading-relaxed">
                                            {currentTexts.desc1}
                                        </p>
                                        <p className="text-slate-400 text-xs leading-relaxed">
                                            {currentTexts.desc2} <Link to="/cookies" className="text-orange-400 hover:text-orange-300 font-medium transition-colors border-b border-orange-400/30 hover:border-orange-300">{currentTexts.cookiePolicy}</Link>.
                                        </p>
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <div className="grid grid-cols-2 gap-3">
                                        <button
                                            onClick={handleAcceptAll}
                                            className="px-4 py-3 bg-white text-slate-900 hover:bg-slate-100 rounded-xl font-bold text-sm transition-all shadow-lg active:scale-[0.98]"
                                        >
                                            {currentTexts.acceptAll}
                                        </button>
                                        <button
                                            onClick={handleRejectAll}
                                            className="px-4 py-3 bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 hover:border-slate-600 rounded-xl font-bold text-sm transition-all active:scale-[0.98]"
                                        >
                                            {currentTexts.rejectAll}
                                        </button>
                                    </div>
                                    <button
                                        onClick={() => setShowSettings(true)}
                                        className="w-full px-4 py-2.5 bg-transparent hover:bg-white/5 text-slate-400 hover:text-white rounded-xl font-semibold text-xs transition-all flex items-center justify-center gap-2 group active:scale-[0.98]"
                                    >
                                        <SettingsIcon className="w-3.5 h-3.5 group-hover:rotate-90 transition-transform duration-500" />
                                        {currentTexts.managePref}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            // Preferences Panel View
                            <div className="p-7 max-h-[80vh] overflow-y-auto custom-scrollbar">
                                <div className="flex justify-between items-center mb-6 sticky top-0 bg-slate-950/95 backdrop-blur-xl py-2 z-10 -mx-2 px-2 border-b border-white/5">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-orange-500/10 rounded-lg">
                                            <SettingsIcon className="w-4 h-4 text-orange-500" />
                                        </div>
                                        <h3 className="text-lg font-bold text-white tracking-tight">{currentTexts.prefTitle}</h3>
                                    </div>
                                    <button
                                        onClick={() => setShowSettings(false)}
                                        className="p-1.5 text-slate-500 hover:text-white transition-colors"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>

                                <p className="text-slate-300 text-sm mb-6 leading-relaxed">
                                    {currentTexts.prefDesc}
                                    <br /><br />
                                    <span className="text-slate-400 text-xs">{currentTexts.necessaryAlert}</span>
                                </p>

                                <div className="space-y-4 mb-8">
                                    {/* Essential Cookies */}
                                    <div className="bg-white/5 p-5 rounded-2xl border border-white/5 group">
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                                <Shield className="w-4 h-4 text-green-500" />
                                                <span className="font-bold text-white text-sm">{currentTexts.necessaryTitle}</span>
                                            </div>
                                            <div className="px-2 py-1 bg-green-500/10 border border-green-500/20 rounded text-[10px] font-bold text-green-500 uppercase tracking-wider">
                                                {currentTexts.alwaysActive}
                                            </div>
                                        </div>
                                        <p className="text-xs text-slate-400 leading-relaxed">
                                            {currentTexts.necessaryDesc}
                                        </p>
                                    </div>

                                    {/* Analytics Cookies */}
                                    <div className={`p-5 rounded-2xl border transition-all ${preferences.analytics ? 'bg-orange-500/5 border-orange-500/20' : 'bg-white/5 border-white/5'}`}>
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                                <div className={`w-2 h-2 rounded-full ${preferences.analytics ? 'bg-orange-500' : 'bg-slate-600'}`} />
                                                <span className="font-bold text-white text-sm">{currentTexts.analyticsTitle}</span>
                                            </div>
                                            <button
                                                onClick={() => setPreferences(prev => ({ ...prev, analytics: !prev.analytics }))}
                                                className={`w-11 h-6 rounded-full p-1 transition-colors relative focus:outline-none focus:ring-2 focus:ring-orange-500/50 ${preferences.analytics ? 'bg-orange-600' : 'bg-slate-700'}`}
                                            >
                                                <div className={`w-4 h-4 bg-white rounded-full transition-transform shadow-sm ${preferences.analytics ? 'translate-x-5' : 'translate-x-0'}`} />
                                            </button>
                                        </div>
                                        <p className="text-xs text-slate-400 leading-relaxed mb-2">
                                            {currentTexts.analyticsDesc}
                                        </p>
                                        <p className="text-[10px] text-orange-400/80 italic">
                                            {currentTexts.analyticsAlert}
                                        </p>
                                    </div>
                                </div>

                                <div className="space-y-3 pt-4 border-t border-white/10">
                                    <button
                                        onClick={handleSavePreferences}
                                        className="w-full px-6 py-3 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-500 hover:to-orange-600 text-white rounded-xl font-bold text-sm transition-all shadow-lg active:scale-[0.98]"
                                    >
                                        {currentTexts.savePref}
                                    </button>
                                    <div className="grid grid-cols-2 gap-3">
                                        <button
                                            onClick={handleAcceptAll}
                                            className="px-4 py-2.5 bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white rounded-xl font-semibold text-xs transition-colors border border-white/5"
                                        >
                                            {currentTexts.acceptAll}
                                        </button>
                                        <button
                                            onClick={handleRejectAll}
                                            className="px-4 py-2.5 bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white rounded-xl font-semibold text-xs transition-colors border border-white/5"
                                        >
                                            {currentTexts.rejectAll}
                                        </button>
                                    </div>
                                </div>
                                <div className="mt-6 text-center">
                                    <p className="text-[10px] text-slate-500">
                                        {currentTexts.withdrawAlert} <Link to="/cookies" className="text-slate-400 hover:text-white underline">{currentTexts.cookiePolicy}</Link> and <Link to="/privacy" className="text-slate-400 hover:text-white underline">{currentTexts.privacyPolicy}</Link>.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default CookieConsent;
